import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { isAuthenticated } from './services/authService';
import CategoriesPage from './pages/inventory/CategoriesPage';
import UnitsPage from './pages/inventory/UnitsPage';
import ProductsPage from './pages/inventory/ProductsPage';
import TaxesPage from './pages/inventory/TaxesPage';
import InventoryReports from './pages/InventoryReports';
import Sales from './components/sales';
import Purchases from './components/purchases';
import Reports from './components/reports';
import People from './components/people';
import CustomerLedger from './pages/reports/CustomerLedger';
import PrivilegesPage from './pages/PrivilegesPage';
import RolePrivilegesPage from './pages/RolePrivilegesPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import NotificationsPage from './pages/NotificationsPage';
import CompanySettings from './pages/settings/CompanySettings';
import PrivilegedRoute from './routes/PrivilegedRoute';
import { NotificationProvider } from './context/NotificationContext';

/**
 * Protected route component to prevent unauthorized access
 * @param {Object} props Component props
 * @param {JSX.Element} props.children Child components to render if authenticated
 * @returns {JSX.Element} Protected route
 */
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/" replace />;
};

/**
 * Route component for login page that redirects to dashboard if already authenticated
 * @param {Object} props Component props
 * @returns {JSX.Element} Login page or redirect
 */
const LoginRoute = () => {
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  if (isAuthenticated()) {
    // When user logs in, ensure their role has dashboard access
    const userRole = localStorage.getItem('userRole');
    if (userRole) {
      try {
        // Get existing privileges or create default ones with dashboard access
        const privilegesData = localStorage.getItem(`role_privileges_${userRole}`);
        if (privilegesData) {
          const privileges = JSON.parse(privilegesData);
          
          // Ensure dashboard access exists
          if (!privileges.accessibleRoutes.includes('/dashboard')) {
            privileges.accessibleRoutes.push('/dashboard');
            localStorage.setItem(`role_privileges_${userRole}`, JSON.stringify(privileges));
          }
        } else if (userRole !== 'ADMIN') {
          // For non-admin roles without privileges, create default with dashboard only
          const defaultPrivileges = {
            visibleMenus: ['dashboard'],
            accessibleRoutes: ['/dashboard']
          };
          localStorage.setItem(`role_privileges_${userRole}`, JSON.stringify(defaultPrivileges));
        }
      } catch (error) {
        console.error('Error setting up default privileges:', error);
      }
    }
    
    return <Navigate to={from} replace />;
  }
  return <LoginPage />;
};

// Default theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

/**
 * Main application component
 * @returns {JSX.Element} The application
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <NotificationProvider>
          <Router>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            <Routes>
              <Route path="/" element={<LoginRoute />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/dashboard" element={
                <PrivilegedRoute path="/dashboard">
                  <DashboardPage />
                </PrivilegedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <PrivilegedRoute path="/admin/dashboard">
                  <DashboardPage />
                </PrivilegedRoute>
              } />
              <Route path="/inventory/categories" element={
                <PrivilegedRoute path="/inventory/categories">
                  <CategoriesPage />
                </PrivilegedRoute>
              } />
              <Route path="/inventory/units" element={
                <PrivilegedRoute path="/inventory/units">
                  <UnitsPage />
                </PrivilegedRoute>
              } />
              <Route path="/inventory/products" element={
                <PrivilegedRoute path="/inventory/products">
                  <ProductsPage />
                </PrivilegedRoute>
              } />
              <Route path="/inventory/taxes" element={
                <PrivilegedRoute path="/inventory/taxes">
                  <TaxesPage />
                </PrivilegedRoute>
              } />
              <Route path="/inventory/reports" element={
                <PrivilegedRoute path="/inventory/reports">
                  <InventoryReports />
                </PrivilegedRoute>
              } />
              <Route path="/sales/*" element={
                <PrivilegedRoute path="/sales/*">
                  <Sales />
                </PrivilegedRoute>
              } />
              <Route path="/purchases/*" element={
                <PrivilegedRoute path="/purchases/*">
                  <Purchases />
                </PrivilegedRoute>
              } />
              <Route path="/reports/*" element={
                <PrivilegedRoute path="/reports/*">
                  <Reports />
                </PrivilegedRoute>
              } />
              <Route path="/people/*" element={
                <PrivilegedRoute path="/people/*">
                  <People />
                </PrivilegedRoute>
              } />
              <Route path="/privileges" element={
                <PrivilegedRoute path="/privileges">
                  <PrivilegesPage />
                </PrivilegedRoute>
              } />
              <Route path="/role-privileges" element={
                <PrivilegedRoute path="/role-privileges">
                  <RolePrivilegesPage />
                </PrivilegedRoute>
              } />
              <Route path="/customer-ledger/:customerId" element={
                <PrivilegedRoute path="/customer-ledger/:customerId">
                  <CustomerLedger />
                </PrivilegedRoute>
              } />
              <Route path="/notifications" element={
                <PrivilegedRoute path="/notifications">
                  <NotificationsPage />
                </PrivilegedRoute>
              } />
              <Route path="/settings/company" element={
                <PrivilegedRoute path="/settings/company">
                  <CompanySettings />
                </PrivilegedRoute>
              } />
              <Route path="/settings" element={
                <PrivilegedRoute path="/settings">
                  <DashboardPage />
                </PrivilegedRoute>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </NotificationProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
